import React from "react";
import { Link } from "react-router-dom";
import { ScrollText, Mail } from "lucide-react";

const LAST_UPDATED = "February 24, 2026";
const CONTACT_EMAIL = "support@umaang.co";
const LEGAL_EMAIL = "legal@umaang.co";
const COMPANY = "Umaang Matrimony Services";
const JURISDICTION_STATE = "Delhi";
const JURISDICTION_COUNTRY = "India";

function Section({ n, title, children }) {
  return (
    <section data-testid={`terms-section-${n}`} className="mt-10">
      <h2 className="font-display text-2xl text-[#1F2937] flex items-baseline gap-3">
        <span className="text-[#C85A53] font-mono text-lg">{String(n).padStart(2, "0")}</span>
        {title}
      </h2>
      <div className="mt-3 text-[#374151] leading-relaxed space-y-3 text-[15px]">
        {children}
      </div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="terms-page">
      <Link to="/" data-testid="terms-back-home" className="text-xs uppercase tracking-widest text-[#C85A53] hover:underline">← Back to Umaang</Link>

      <div className="mt-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl grid place-items-center bg-[#FEF3C7] text-[#B45309]">
          <ScrollText className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-display text-4xl">Terms of Service</h1>
          <p className="text-xs text-[#57534E] mt-1">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-[#FEE2E2] to-[#FCE7F3] border border-[#FCA5A5]">
        <p className="text-sm text-[#7F1D1D]">
          <b>Important:</b> Umaang is only for adults <b>18 years or older</b> who are seeking a marriage partner. Casual dating, escort services, or any illegal activity is strictly prohibited and will result in immediate ban.
        </p>
      </div>

      <Section n={1} title="Acceptance of terms">
        <p>These Terms of Service ("Terms") form a binding agreement between you and {COMPANY} ("Umaang", "we", "us"). By creating an account or otherwise using www.umaang.co or the Umaang mobile app (the "Service") you accept these Terms and our <Link to="/privacy" className="text-[#C85A53] hover:underline">Privacy Policy</Link>.</p>
        <p>If you do not agree, do not use the Service.</p>
      </Section>

      <Section n={2} title="Eligibility">
        <ul className="list-disc pl-6 space-y-1">
          <li>You must be at least <b>18 years old</b>.</li>
          <li>You must be legally competent to marry under the laws of India.</li>
          <li>You must not currently be married (unless legally divorced or widowed and eligible to remarry).</li>
          <li>You must not have been convicted of a sexual offence, domestic violence, harassment, or fraud.</li>
          <li>You must not be on any government sanctions list.</li>
        </ul>
        <p>We may verify eligibility through Aadhaar / PAN KYC (via Digio) at any time. Providing false eligibility information will result in permanent ban and forfeiture of any Premium fees paid.</p>
      </Section>

      <Section n={3} title="Your account">
        <ul className="list-disc pl-6 space-y-1">
          <li>You are responsible for all activity on your account.</li>
          <li>You will keep your password confidential and notify us immediately of any unauthorised access.</li>
          <li>You will provide accurate, current, and complete information — including your real name, age, marital status, photos of yourself, and contact details.</li>
          <li>You will create <b>only one</b> account. Duplicate or impersonation accounts will be removed.</li>
          <li>You will not sell, rent, or transfer your account to anyone else.</li>
        </ul>
      </Section>

      <Section n={4} title="Acceptable use — what you must not do">
        <p>You will not, and will not attempt to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Post fake, misleading, or defamatory profile information or photos.</li>
          <li>Impersonate any other person or claim a false marital status.</li>
          <li>Use Umaang for casual dating, hookups, escort services, sex work, or any commercial solicitation.</li>
          <li>Send obscene, abusive, threatening, harassing, hateful, or sexually explicit messages or images.</li>
          <li>Ask other members for money, gifts, loans, cryptocurrency, or "processing fees".</li>
          <li>Share or request contact details (phone/WhatsApp/social handles) to circumvent our messaging system before both parties consent.</li>
          <li>Collect, scrape, index, or copy other members' information without written consent.</li>
          <li>Upload viruses, malware, or any code designed to harm the Service.</li>
          <li>Interfere with the Service, attempt to breach security, reverse engineer, or run automated bots/scripts.</li>
          <li>Use Umaang if you are on any sanctions list or barred by law from using dating/matrimony services.</li>
          <li>Violate any Indian or applicable foreign law.</li>
        </ul>
        <p>Violations may result in warning, feature restriction, account suspension, permanent ban, and where warranted, reporting to law enforcement.</p>
      </Section>

      <Section n={5} title="Verification (KYC)">
        <p>To keep Umaang safe we may require Aadhaar and/or PAN verification via <b>Digio</b> (an RBI-licensed AUA). Verification is mandatory for certain features (e.g., sending unlimited requests, unlocking Premium). You authorise Digio to verify your identity and share the verification outcome with Umaang. We store only the outcome, not your raw Aadhaar/PAN images.</p>
      </Section>

      <Section n={6} title="Content ownership & licence">
        <p>You retain ownership of everything you upload (photos, biography, preferences). By uploading, you grant Umaang a non-exclusive, worldwide, royalty-free licence to host, display, cache, resize, and moderate your content solely to operate the Service. This licence ends when you delete the content (with reasonable delay for backups).</p>
        <p>You confirm that you own the rights to any content you upload, that it does not violate any third-party's rights, and that all photos are of you (not celebrities, models, or other people).</p>
      </Section>

      <Section n={7} title="Premium subscriptions & payments">
        <ul className="list-disc pl-6 space-y-1">
          <li>Umaang offers free and paid Premium plans (Silver, Gold, Platinum, Diamond).</li>
          <li>Payments are processed by Razorpay. Prices shown include applicable GST unless otherwise stated.</li>
          <li>Subscriptions activate immediately on successful payment and last for the stated duration (e.g., 30 days, 90 days).</li>
          <li>Subscriptions do <b>not</b> auto-renew by default. You must repurchase at expiry.</li>
          <li>Promotional discounts (like the 50% OFF launch offer) apply only when the offer is live and cannot be combined with other offers.</li>
        </ul>
        <p><b>Refunds:</b> Digital services are non-refundable once activated except where required by law. If you experience a billing error or a duplicate charge, email {CONTACT_EMAIL} within 7 days with your Razorpay reference ID — verified errors will be refunded to the original payment method within 10 working days.</p>
      </Section>

      <Section n={8} title="Refer-and-earn">
        <p>Referral rewards are credited only when the referred person completes Aadhaar KYC and remains an active member for at least 30 days. Self-referrals, duplicate accounts, and fraud (fake KYC, disposable numbers) will forfeit all rewards and may lead to ban.</p>
      </Section>

      <Section n={9} title="No guarantee of match or marriage">
        <p>Umaang is a matchmaking platform, not a marriage guarantor. We do not guarantee that you will find a partner, receive replies, or that any conversation will lead to marriage. Match percentages and Guna Milan scores are informational only and are not medical, astrological, or legal advice.</p>
      </Section>

      <Section n={10} title="Safety & user diligence">
        <p>While Umaang runs KYC and moderates reports, we cannot fully verify the intentions of every member. <b>Please:</b></p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Never send money, gifts, or crypto to anyone you meet on Umaang.</li>
          <li>Meet in public places for the first few meetings, with family aware.</li>
          <li>Verify claims (education, job, family) independently before commitment.</li>
          <li>Use the in-app "Report" and "Block" tools if you see anything suspicious.</li>
        </ul>
      </Section>

      <Section n={11} title="Termination">
        <p>You may delete your account any time from Profile → Settings. We may suspend or terminate your account, with or without notice, if you violate these Terms, harm other members, or breach the law. On termination your profile is de-listed and data erased per our Privacy Policy retention rules.</p>
      </Section>

      <Section n={12} title="Disclaimers">
        <p>The Service is provided "as is" and "as available" without warranties of any kind, express or implied, including merchantability, fitness for purpose, or non-infringement. We do not warrant uninterrupted, error-free operation, or that any specific member is genuine, single, or suitable for you.</p>
      </Section>

      <Section n={13} title="Limitation of liability">
        <p>To the maximum extent permitted by law, Umaang and its officers, employees, and partners are not liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, goodwill, or opportunity, arising out of or related to the Service. Our total aggregate liability for any claim shall not exceed the greater of (a) the amount you paid Umaang in the 12 months before the claim, or (b) ₹5,000.</p>
      </Section>

      <Section n={14} title="Indemnity">
        <p>You agree to indemnify and hold Umaang harmless from any claim, damage, loss, liability, or expense (including reasonable legal fees) arising out of your breach of these Terms, misuse of the Service, or infringement of any third-party right.</p>
      </Section>

      <Section n={15} title="Intellectual property">
        <p>The Umaang name, logo, marks, text, graphics, code, and features are the property of {COMPANY} and are protected by copyright, trademark, and other laws. You may not copy, reproduce, or reuse them without written permission.</p>
      </Section>

      <Section n={16} title="Governing law & dispute resolution">
        <p>These Terms are governed by the laws of {JURISDICTION_COUNTRY}. Any dispute arising out of or in connection with the Service shall first be attempted to be resolved by good-faith negotiation. Unresolved disputes shall be submitted to binding arbitration by a sole arbitrator in {JURISDICTION_STATE}, {JURISDICTION_COUNTRY}, under the Arbitration and Conciliation Act, 1996. The courts of {JURISDICTION_STATE}, {JURISDICTION_COUNTRY} shall have exclusive jurisdiction for interim reliefs.</p>
      </Section>

      <Section n={17} title="Changes to these Terms">
        <p>We may update these Terms occasionally. Material changes will be notified by email or in-app banner at least 15 days in advance. Continued use after the effective date constitutes acceptance.</p>
      </Section>

      <Section n={18} title="Contact & grievance">
        <div className="p-4 bg-[#F9FAFB] border border-[#D6D2C9] rounded-xl">
          <p className="text-sm"><b>{COMPANY}</b></p>
          <p className="text-sm flex items-center gap-2 mt-1"><Mail className="w-3.5 h-3.5" /> Support: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#C85A53] hover:underline">{CONTACT_EMAIL}</a></p>
          <p className="text-sm flex items-center gap-2 mt-1"><Mail className="w-3.5 h-3.5" /> Legal: <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#C85A53] hover:underline">{LEGAL_EMAIL}</a></p>
          <p className="text-xs text-[#57534E] mt-2">Grievance response time: within 15 working days under IT Rules 2021 and DPDP Act 2023.</p>
        </div>
      </Section>

      <div className="mt-16 pt-6 border-t border-[#D6D2C9] flex items-center justify-between text-xs text-[#57534E]">
        <span>© {new Date().getFullYear()} {COMPANY}</span>
        <div className="flex gap-4">
          <Link to="/privacy" data-testid="terms-to-privacy" className="hover:underline">Privacy Policy</Link>
          <Link to="/" className="hover:underline">Home</Link>
        </div>
      </div>
    </div>
  );
}
